/**
 * Internal AI Task Service - React Query Integration
 * Handles all internal AI task-related API communications
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import {
  InternalAITask,
  InternalAITaskWithModels,
  InternalAITaskModel,
  CreateInternalAITaskInput,
  UpdateInternalAITaskInput,
  CreateInternalAITaskModelInput,
  UpdateInternalAITaskModelInput,
} from '../types/admin';
import { AxiosError } from 'axios';
import { useNotification } from '../hooks/useNotification';

// ============================================
// API Response Types
// ============================================

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// ============================================
// Query Keys
// ============================================

export const internalAITaskKeys = {
  all: ['internal-ai-tasks'] as const,
  lists: () => [...internalAITaskKeys.all, 'list'] as const,
  list: (filters: string) => [...internalAITaskKeys.lists(), { filters }] as const,
  details: () => [...internalAITaskKeys.all, 'detail'] as const,
  detail: (publicId: string) => [...internalAITaskKeys.details(), publicId] as const,
};

// ============================================
// API Functions - Tasks
// ============================================

/**
 * Fetches all internal AI tasks from the backend
 */
const fetchInternalAITasks = async (): Promise<InternalAITask[]> => {
  try {
    const response = await axiosInstance.get<ApiResponse<InternalAITask[]>>(
      '/v1/admin/internal-ai-tasks'
    );
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching internal AI tasks:', error);
    throw error;
  }
};

/**
 * Fetches a single internal AI task by publicId with its models
 */
const fetchInternalAITask = async (publicId: string): Promise<InternalAITaskWithModels> => {
  try {
    const response = await axiosInstance.get<ApiResponse<InternalAITaskWithModels>>(
      `/v1/admin/internal-ai-tasks/${publicId}`
    );
    return response.data.data as InternalAITaskWithModels;
  } catch (error) {
    console.error('Error fetching internal AI task:', error);
    throw error;
  }
};

/**
 * Creates a new internal AI task
 */
const createInternalAITask = async (input: CreateInternalAITaskInput): Promise<InternalAITask> => {
  try {
    const response = await axiosInstance.post<ApiResponse<InternalAITask>>(
      '/v1/admin/internal-ai-tasks',
      input
    );
    return response.data.data as InternalAITask;
  } catch (error) {
    console.error('Error creating internal AI task:', error);
    throw error;
  }
};

/**
 * Updates an existing internal AI task
 */
const updateInternalAITask = async ({
  publicId,
  ...input
}: UpdateInternalAITaskInput): Promise<InternalAITask> => {
  try {
    const response = await axiosInstance.patch<ApiResponse<InternalAITask>>(
      `/v1/admin/internal-ai-tasks/${publicId}`,
      input
    );
    return response.data.data as InternalAITask;
  } catch (error) {
    console.error('Error updating internal AI task:', error);
    throw error;
  }
};

/**
 * Deletes an internal AI task
 */
const deleteInternalAITask = async (publicId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/v1/admin/internal-ai-tasks/${publicId}`);
  } catch (error) {
    console.error('Error deleting internal AI task:', error);
    throw error;
  }
};

/**
 * Updates internal AI task enabled status
 */
const updateInternalAITaskStatus = async ({
  publicId,
  enabled,
}: {
  publicId: string;
  enabled: boolean;
}): Promise<InternalAITask> => {
  try {
    const response = await axiosInstance.patch<ApiResponse<InternalAITask>>(
      `/v1/admin/internal-ai-tasks/${publicId}/status`,
      { enabled }
    );
    return response.data.data as InternalAITask;
  } catch (error) {
    console.error('Error updating internal AI task status:', error);
    throw error;
  }
};

// ============================================
// API Functions - Task Models
// ============================================

/**
 * Creates a new task model
 */
const createInternalAITaskModel = async (
  input: CreateInternalAITaskModelInput
): Promise<InternalAITaskModel> => {
  try {
    const response = await axiosInstance.post<ApiResponse<InternalAITaskModel>>(
      `/v1/admin/internal-ai-tasks/${input.taskPublicId}/models`,
      {
        llmModelPublicId: input.llmModelPublicId,
        modelName: input.modelName,
        weight: input.weight,
        priority: input.priority,
        temperature: input.temperature,
        maxTokens: input.maxTokens,
        paramOverrides: input.paramOverrides,
        config: input.config,
      }
    );
    return response.data.data as InternalAITaskModel;
  } catch (error) {
    console.error('Error creating internal AI task model:', error);
    throw error;
  }
};

/**
 * Updates an existing task model
 */
const updateInternalAITaskModel = async ({
  publicId,
  ...input
}: UpdateInternalAITaskModelInput): Promise<InternalAITaskModel> => {
  try {
    const response = await axiosInstance.patch<ApiResponse<InternalAITaskModel>>(
      `/v1/admin/internal-ai-tasks/models/${publicId}`,
      input
    );
    return response.data.data as InternalAITaskModel;
  } catch (error) {
    console.error('Error updating internal AI task model:', error);
    throw error;
  }
};

/**
 * Deletes a task model
 */
const deleteInternalAITaskModel = async (publicId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/v1/admin/internal-ai-tasks/models/${publicId}`);
  } catch (error) {
    console.error('Error deleting internal AI task model:', error);
    throw error;
  }
};

// ============================================
// React Query Hooks - Tasks
// ============================================

/**
 * Hook to fetch all internal AI tasks
 */
export const useInternalAITasks = () => {
  return useQuery({
    queryKey: internalAITaskKeys.lists(),
    queryFn: fetchInternalAITasks,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch a single internal AI task by publicId
 */
export const useInternalAITask = (publicId: string) => {
  return useQuery({
    queryKey: internalAITaskKeys.detail(publicId),
    queryFn: () => fetchInternalAITask(publicId),
    enabled: !!publicId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to create a new internal AI task
 */
export const useCreateInternalAITask = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: createInternalAITask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: internalAITaskKeys.lists() });
      notification.success('Internal AI task created successfully');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.error || 'Failed to create internal AI task';
      notification.error(message);
      console.error('Error creating internal AI task:', error);
    },
  });
};

/**
 * Hook to update an existing internal AI task
 */
export const useUpdateInternalAITask = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: updateInternalAITask,
    onSuccess: (updatedTask) => {
      queryClient.setQueryData<InternalAITask[]>(internalAITaskKeys.lists(), (current) =>
        current?.map((task) =>
          task.publicId === updatedTask.publicId ? { ...task, ...updatedTask } : task
        )
      );
      queryClient.setQueryData<InternalAITaskWithModels>(
        internalAITaskKeys.detail(updatedTask.publicId),
        (current) => (current ? { ...current, ...updatedTask } : current)
      );
      queryClient.invalidateQueries({ queryKey: internalAITaskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: internalAITaskKeys.detail(updatedTask.publicId) });
      notification.success('Internal AI task updated successfully');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.error || 'Failed to update internal AI task';
      notification.error(message);
      console.error('Error updating internal AI task:', error);
    },
  });
};

/**
 * Hook to delete an internal AI task
 */
export const useDeleteInternalAITask = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: deleteInternalAITask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: internalAITaskKeys.lists() });
      notification.success('Internal AI task deleted successfully');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.error || 'Failed to delete internal AI task';
      notification.error(message);
      console.error('Error deleting internal AI task:', error);
    },
  });
};

/**
 * Hook to toggle internal AI task enabled status with optimistic updates
 */
export const useToggleInternalAITaskStatus = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: updateInternalAITaskStatus,
    // Optimistic update
    onMutate: async (variables: { publicId: string; enabled: boolean }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: internalAITaskKeys.lists() });

      // Snapshot previous value
      const previousTasks = queryClient.getQueryData<InternalAITask[]>(internalAITaskKeys.lists());

      // Optimistically update
      if (previousTasks) {
        queryClient.setQueryData<InternalAITask[]>(
          internalAITaskKeys.lists(),
          previousTasks.map((task) =>
            task.publicId === variables.publicId ? { ...task, enabled: variables.enabled } : task
          )
        );
      }

      return { previousTasks };
    },
    // Rollback on error
    onError: (
      error: AxiosError<ApiResponse>,
      variables,
      context: { previousTasks?: InternalAITask[] } | undefined
    ) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(internalAITaskKeys.lists(), context.previousTasks);
      }
      const message = error.response?.data?.error || 'Failed to update internal AI task status';
      notification.error(message);
      console.error('Error toggling internal AI task status:', error);
    },
    // Refetch on success
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: internalAITaskKeys.lists() });
      notification.success(
        `Internal AI task ${variables.enabled ? 'enabled' : 'disabled'} successfully`
      );
    },
  });
};

// ============================================
// React Query Hooks - Task Models
// ============================================

/**
 * Hook to create a new task model
 */
export const useCreateInternalAITaskModel = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: createInternalAITaskModel,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: internalAITaskKeys.detail(variables.taskPublicId),
      });
      queryClient.invalidateQueries({ queryKey: internalAITaskKeys.lists() });
      notification.success('Task model created successfully');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.error || 'Failed to create task model';
      notification.error(message);
      console.error('Error creating task model:', error);
    },
  });
};

/**
 * Hook to update an existing task model
 */
export const useUpdateInternalAITaskModel = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: updateInternalAITaskModel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: internalAITaskKeys.all });
      notification.success('Task model updated successfully');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.error || 'Failed to update task model';
      notification.error(message);
      console.error('Error updating task model:', error);
    },
  });
};

/**
 * Hook to delete a task model
 */
export const useDeleteInternalAITaskModel = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: deleteInternalAITaskModel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: internalAITaskKeys.all });
      notification.success('Task model deleted successfully');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.error || 'Failed to delete task model';
      notification.error(message);
      console.error('Error deleting task model:', error);
    },
  });
};
